import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import logoImg from '@/logo.svg'

interface AboutDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const APP_VERSION = '1.0.19'

const CHANGELOG = [
  {
    version: '1.0.19',
    date: '2025-12-06',
    changes: [
      'Fixed: crypto.randomUUID error on HTTP sites (not available in insecure contexts)',
      'Added: generateId utility that works on both HTTP and HTTPS',
    ],
  },
  {
    version: '1.0.18',
    date: '2025-12-06',
    changes: [
      'Added: HTTPS printing support for sites served via HTTPS',
      'Added: HTTPS option in Settings for secure printing',
      'Fixed: Mixed Content error when printing from HTTPS sites',
    ],
  },
  {
    version: '1.0.17',
    date: '2025-12-06',
    changes: [
      'Added: HTTP printing via NEO ZPL Simulator proxy',
      'Added: Connection type selector in Settings (TCP Direct vs HTTP)',
      'Added: Browser can now print via Simulator HTTP endpoint',
    ],
  },
  {
    version: '1.0.16',
    date: '2025-12-05',
    changes: [
      'Added: Right-click context menu on elements with layer ordering and actions',
    ],
  },
  {
    version: '1.0.15',
    date: '2025-12-05',
    changes: [
      'Added: Field Reverse (^FR) support for text, barcodes, QR codes, and DataMatrix elements',
      'Added: Print preview on regular printer for testing purposes',
    ],
  },
  {
    version: '1.0.12',
    date: '2025-12-05',
    changes: [
      'Added: GTIN check digit validation with visual warning for AI 01 and AI 02',
      'Fixed: Multi-element drag with mouse now works correctly',
      'Improved: Replaced browser alerts with styled notification dialogs',
    ],
  },
  {
    version: '1.0.11',
    date: '2025-12-05',
    changes: [
      'Added: Multi-element drag - select multiple elements with Ctrl+click and drag them together',
      'Added: Arrow keys now move all selected elements when multiple are selected',
    ],
  },
  {
    version: '1.0.10',
    date: '2025-12-05',
    changes: [
      'Added: Resizable Properties Panel - drag the top border to adjust height',
      'Added: Panel height is persisted across sessions',
    ],
  },
  {
    version: '1.0.9',
    date: '2025-12-05',
    changes: [
      'Added: Copy/Paste elements with Ctrl+C/Ctrl+V keyboard shortcuts',
      'Added: Duplicate element with Ctrl+D keyboard shortcut',
      'Added: Multi-element copy/paste support',
      'Fixed: Manual page now opens correctly in Electron app',
    ],
  },
  {
    version: '1.0.8',
    date: '2025-12-05',
    changes: [
      'Added: Export/Import designs as JSON files for backup and sharing',
      'Added: Export individual designs or all designs at once',
      'Added: About dialog with version info and changelog',
      'Added: Version display in application header',
      'Updated: Manual documentation (EN/PT) with export/import instructions',
    ],
  },
  {
    version: '1.0.7',
    date: '2025-12-05',
    changes: [
      'Added: Export/Import JSON functionality in Saved Designs modal',
      'Fixed: Copy/Paste (Ctrl+C/V) now works in Electron app',
    ],
  },
  {
    version: '1.0.6',
    date: '2025-12-05',
    changes: [
      'Fixed: Manual page not opening when running in Docker',
      'Changed: Switched from serve to nginx for static file serving',
    ],
  },
  {
    version: '1.0.5',
    date: '2025-12-05',
    changes: [
      'Added: Custom application icon for Windows Electron build',
      'Added: NSIS installer and portable builds for Windows',
    ],
  },
  {
    version: '1.0.4',
    date: '2025-12-04',
    changes: [
      'Added: Electron desktop application support',
      'Added: Windows build with installer',
    ],
  },
  {
    version: '1.0.3',
    date: '2025-12-04',
    changes: [
      'Added: GS1-128 barcode support with Application Identifiers',
      'Added: Variable support for GS1 AIs',
    ],
  },
  {
    version: '1.0.2',
    date: '2025-12-03',
    changes: [
      'Added: GS1 DataMatrix support with Application Identifiers',
      'Added: AI ordering and validation',
    ],
  },
  {
    version: '1.0.1',
    date: '2025-12-02',
    changes: [
      'Added: Image element support with automatic conversion to ZPL',
      'Added: Discard label button',
      'Improved: Grid snapping',
    ],
  },
  {
    version: '1.0.0',
    date: '2025-12-01',
    changes: [
      'Initial release',
      'Visual WYSIWYG label editor',
      'Real-time ZPL code generation',
      'Live preview via Labelary API',
      'Support for 1D/2D barcodes',
      'Variable data support',
      'Direct printing via USB/Network',
    ],
  },
]

export function AboutDialog({ open, onOpenChange }: AboutDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>About NEO ZPL Designer</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Logo and Version */}
          <div className="flex items-center gap-4">
            <img src={logoImg} alt="NEO" className="h-16" />
            <div>
              <h2 className="text-xl font-semibold">NEO ZPL Designer</h2>
              <p className="text-sm text-muted-foreground">
                Version {APP_VERSION}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Visual Label Designer for Zebra Printers
              </p>
            </div>
          </div>

          <Separator />

          {/* Changelog */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Changelog</h3>
            <ScrollArea className="h-[300px] border rounded-md p-3">
              {CHANGELOG.map((release, index) => (
                <div key={release.version} className={index > 0 ? 'mt-4' : ''}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">v{release.version}</span>
                    <span className="text-xs text-muted-foreground">
                      {release.date}
                    </span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5 ml-2">
                    {release.changes.map((change, i) => (
                      <li key={i}>• {change}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </ScrollArea>
          </div>

          <Separator />

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground">
            <p>&copy; 2025 NEO ZPL Designer. All rights reserved.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { APP_VERSION }
